/**
 * Record counts for the nav leaves that asked for a badge (DP-6112).
 *
 * One `batch-aggregate` call per badged leaf, keyed only on the leaf's entity
 * and its `countCondition`. That key is deliberately NOT encore-grid's
 * `useBatchAggregate` key: the grid inlines its whole request envelope — which
 * folds in the live `filterInfo` — so sharing the entry would make the nav
 * badge move every time the user filters the entity page. The requirement is
 * satisfied as same endpoint, same entity, same condition; not same key.
 *
 * `useQueries` rather than the toolkit's `useApiQuery` because the number of
 * badged leaves is whatever the modules config says, and hooks cannot be called
 * in a loop. The request still goes through the endpoint registry and the
 * shared `apiClient`, the same way `files/useFileUpload.ts` does.
 *
 * The leaf's `countCondition` is **evaluated before it is posted** (DP-6140).
 * It used to go out verbatim, so a leaf scoped to the viewer
 * (`=currentUser.id`) counted every actor's rows — which the shipped
 * notification badge already does, its condition being `status EQ UNREAD` with
 * no actor term at all. The evaluator is `resolveConditionExpressions`,
 * exported by `encore-grid`: the same `=` convention, the same FEEL engine and
 * the same pending semantics the grid itself resolves a base condition with, so
 * the badge and the page it links to can never disagree about the viewer's
 * scope. A second implementation here is what that export exists to prevent.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useAuth, endpointRegistry } from '@locus-taxy/encore-ui-network';
import { resolveConditionExpressions } from '@locus-taxy/encore-grid';
import type { Condition, ConditionContext } from '@locus-taxy/encore-grid';
import type { ModuleLeafRoute } from '../../utils/modulesNavigation';
import { navBadgeKey } from '../../utils/navBadge';
import { useCurrentUser } from './useCurrentUser';

/** Doubles as the request id and the aggregation alias — one of each per call. */
const NAV_COUNT_ID = 'navCount';

/** Matches `useModules`: the nav and its badges go stale together. */
const NAV_COUNT_STALE_TIME_MS = 5 * 60 * 1000;

const MS_PER_SECOND = 1000;

/**
 * The leaf's own cadence in milliseconds, or undefined for "do not poll".
 *
 * Read off the module rather than off the entity's grid metadata: the badge is
 * the nav's affordance, and an entity whose list refreshes every 30 seconds is
 * not thereby asking every module pointing at it to poll. A module that wants a
 * live count says so on itself (`countRefetchInterval`).
 */
export function navCountRefetchInterval(
  seconds: number | undefined
): number | undefined {
  return typeof seconds === 'number' && seconds > 0
    ? seconds * MS_PER_SECOND
    : undefined;
}

interface BatchAggregateResponse {
  data?: {
    results?: Record<
      string,
      { metrics?: Record<string, number>; success?: boolean }
    >;
  };
}

/** The leaf fields a count needs, narrowed off `ModuleLeafRoute`. */
export interface BadgedLeaf {
  entityName: string;
  countCondition?: Condition;
  countRefetchInterval?: number;
}

/**
 * The ENTITY leaves that asked for a count. A leaf without `showCount` is not
 * in the list, so it produces no query and therefore no request.
 */
export function toBadgedLeaves(leaves: ModuleLeafRoute[]): BadgedLeaf[] {
  const badged: BadgedLeaf[] = [];
  for (const leaf of leaves) {
    if (leaf.type === 'ENTITY' && leaf.showCount && leaf.entityName) {
      badged.push({
        entityName: leaf.entityName,
        countCondition: leaf.countCondition,
        countRefetchInterval: leaf.countRefetchInterval,
      });
    }
  }
  return badged;
}

/**
 * The POST envelope for one leaf's count.
 *
 * `includeGlobalEntities` belongs INSIDE `aggregation`, never on the outer
 * request: `AggregationRequest` *is* that object (its `CriteriaQuery` is
 * `@JsonUnwrapped`, which is why `condition` sits directly under it), so the
 * flag on the envelope is an unrecognised key — silently dropped, never
 * rejected, and the count comes back wrong. Without it the badge counts only
 * the rows inside the actor's own scope while the entity page also lists the
 * untagged GLOBAL rows every scope can see.
 */
export function buildNavCountBody(countCondition?: Condition) {
  return {
    request: {
      requests: [
        {
          id: NAV_COUNT_ID,
          aggregation: {
            includeGlobalEntities: true,
            ...(countCondition && { condition: countCondition }),
            aggregations: [{ type: 'COUNT' as const, alias: NAV_COUNT_ID }],
          },
        },
      ],
    },
  };
}

/** The single metric out of one response, or undefined if it did not arrive. */
export function readNavCount(
  response: BatchAggregateResponse | undefined
): number | undefined {
  const result = response?.data?.results?.[NAV_COUNT_ID];
  return result?.success ? result.metrics?.[NAV_COUNT_ID] : undefined;
}

/**
 * `badgeKey` → count, for every badged leaf whose count has landed.
 *
 * A failed count is simply absent, leaving the leaf unbadged. There is no error
 * toast: a nav ornament failing is not worth interrupting the user, and one
 * toast per badged leaf on an outage would be several.
 */
export function useNavBadgeCounts(
  clientId: string,
  leaves: ModuleLeafRoute[]
): Record<string, number> {
  const { apiClient } = useAuth();
  const { data: me } = useCurrentUser(clientId);
  const badged = useMemo(() => toBadgedLeaves(leaves), [leaves]);
  const context = useMemo<ConditionContext>(
    () => ({
      currentUser: me?.data?.entityData as ConditionContext['currentUser'],
    }),
    [me]
  );

  /*
   * Resolved here, not in `queryFn`: the outcome decides both what goes on the
   * wire and whether anything goes at all, and `enabled` is read during render.
   *
   * MEMOIZED because this walks the condition tree and runs the FEEL engine
   * once per badged leaf, and `useAppNavigation` re-renders on every route
   * change and on every count that arrives. Nothing refetches without it —
   * react-query hashes the key by value, so a fresh object each render is
   * still the same key — it is simply the work. `encore-grid` memoizes the
   * identical call in both of its own consumers (`useResolvedBaseCondition`,
   * `useResolvedViewExpressions`); this follows them.
   */
  const resolved = useMemo(
    () =>
      badged.map(leaf => ({
        leaf,
        ...resolveConditionExpressions(leaf.countCondition, context),
      })),
    [badged, context]
  );

  return useQueries({
    queries: resolved.map(({ leaf, condition, pending }) => {
      return {
        // Keyed on the RESOLVED condition. Two actors share a client and would
        // otherwise share one cache entry for `=currentUser.id`, so whoever
        // asked first would hand the other their own count.
        queryKey: [
          'navBadgeCount',
          clientId,
          leaf.entityName,
          condition ?? null,
        ],
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          apiClient.post<BatchAggregateResponse>(
            endpointRegistry.getEndpoint('entity.batch-aggregate', {
              clientId,
              entityName: leaf.entityName,
            }),
            buildNavCountBody(condition),
            { requireAuth: true, signal }
          ),
        enabled: Boolean(clientId) && !pending,
        staleTime: NAV_COUNT_STALE_TIME_MS,
        // Absent cadence = no polling, which is what the badge did before any
        // module declared one.
        ...(navCountRefetchInterval(leaf.countRefetchInterval) !==
          undefined && {
          refetchInterval: navCountRefetchInterval(leaf.countRefetchInterval),
          refetchIntervalInBackground: false,
        }),
      };
    }),
    combine: (results: Array<{ data?: BatchAggregateResponse }>) =>
      badged.reduce<Record<string, number>>((counts, leaf, index) => {
        const count = readNavCount(results[index]?.data);
        if (count !== undefined) {
          // The RAW condition, deliberately: `modulesNavigation` stamps
          // `badgeKey` off the wire value while building the tree, with no
          // access to `/me`. Keying the count by the resolved condition here
          // would join against a string the nav side can never produce.
          counts[navBadgeKey(leaf.entityName, leaf.countCondition)] = count;
        }
        return counts;
      }, {}),
  });
}
